import axios from 'axios';
import { API_BASE_URL } from '../config';
import * as authService from './authService';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor de request - añadir token y verificar expiración
api.interceptors.request.use(
  (config) => {
    const url = config?.url ?? '';
    if (!url.includes('Auth/login') && !url.includes('Auth/validate') && !url.includes('Auth/refresh')) {
      const savedUser = authService.getCurrentUser();
      const expiresAt = savedUser?.expiresAt ? new Date(savedUser.expiresAt).getTime() : 0;
      if (expiresAt && Date.now() >= expiresAt) {
        authService.clearSession();
        window.location.href = '/login';
        return Promise.reject(new axios.Cancel('Token expired'));
      }
    }
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response - manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';

    if (!error.response) {
      const msg = error.message || 'No se pudo conectar con el servidor.';
      error.message = msg;
      error.customMessage = msg;
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      !url.includes('Auth/login') &&
      !url.includes('Auth/validate') &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const response = await authService.refreshToken();
        originalRequest.headers.Authorization = `Bearer ${response.token}`;
        return api(originalRequest);
      } catch (refreshError) {
        authService.clearSession();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const data = error.response?.data;

    let message = 'Ocurrió un error inesperado.';

    if (typeof data === 'string') message = data;
    // `message` va primero porque es lo que devuelven los controladores de Legal365
    // (`BadRequest(new { message })`). Sin esta rama, todo BadRequest llegaba a la vista como
    // "Ocurrió un error inesperado.": el backend explicaba el problema y el usuario no lo veía.
    else if (data?.message) message = data.message;
    else if (data?.detail) message = data.detail;
    else if (data?.title) message = data.title;
    else if (data?.errors && typeof data.errors === 'object') {
      message = Object.values(data.errors).flat().join('\n');
    }

    error.message = message;
    error.customMessage = message;

    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data.Message = error.response.data.Message || message;
      error.response.data.message = error.response.data.message || message;
    }

    return Promise.reject(error);
  }
);

export { api };
export default api;
